import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {MachineCategory} from '@models/adminstration/portal-setup/machine-category/machine-category';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NotificationService} from '@services/notification/notification.service';
import {FileTypes} from '@constants/file-types';
import {MachineService} from '@services/administration/machine/machine.service';
import {Machine} from '@models/machine/machine';
import {CategoryColumn} from '@models/adminstration/portal-setup/machine-category/category-column';
import {
  MachineCategoryColumnConfiguration
} from '@models/adminstration/portal-setup/machine-category/machine-category-column-configuration';
import {
  MachineCategoryBaseColumnTypes
} from '@constants/administration/portal-setup/machine-category/machine-category-base-column-types';
import {MachineCategoryService} from '@services/administration/portal-setup/machine-category/machine-category.service';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {DragulaService} from 'ng2-dragula';
import {forkJoin, Observable, of, Subscription} from 'rxjs';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {ValidatorsService} from '@services/validators/validators.service';
import {TranslateService} from '@ngx-translate/core';
import {MetaData} from '@models/adminstration/meta-data';
import {MetadataElasticSearchService} from '@services/elastic-search/metadata/metadata-elastic-search.service';

@Component({
  selector: 'scitis-machine-category-edit',
  templateUrl: './machine-category-edit.component.html',
  styleUrls: ['./machine-category-edit.component.scss'],
  standalone: false
})
export class MachineCategoryEditComponent implements OnInit {

  @Input()
  public isInEditMode: boolean = false;
  @Input()
  public machineCategory: MachineCategory = new MachineCategory();
  @Input()
  public pageTitle: string = '';
  @Input()
  public columnConfiguration = new MachineCategoryColumnConfiguration();
  @Input()
  public existingCategoryNames: string[] = [];

  public machineCategoryEditForm: UntypedFormGroup = new UntypedFormGroup({});
  public machinesByCategory: Machine[] = [];
  public isSaveButtonDisabled: boolean = true;
  public imageFileTypes: FileTypes[] = [FileTypes.PNG, FileTypes.JPG];
  public maxImageHeight: number = 300;
  public maxImageWidth: number = 300;
  public maxFileSize: number = 30_000;
  public isLoadingMachinesByCategory: boolean = false;
  public dragAndDropColumns = 'COLUMNS';
  public subscription = new Subscription();
  public isLoadingColumnConfig: boolean = false;
  public isColumnFormInvalid: boolean = false;
  public metaDataForRefMachine: MetaData[] = [];
  public signalsForRefMachine: any = {};
  public isLoadingMachine: boolean = false;

  private COMPONENT_NAME: string = 'MachineCategoryEditComponent';
  private readonly MACHINE_NAME_COLUMN = 'machineName';
  private readonly NEW_COLUMN_PLACEHOLDER: string;

  constructor(
      private activeModal: NgbActiveModal,
      private machineService: MachineService,
      private translate: TranslateService,
      private machineCategoryService: MachineCategoryService,
      private dragulaService: DragulaService,
      private elasticSearchService: ElasticSearchService,
      private validators: ValidatorsService,
      private notification: NotificationService,
      private elasticMetaDataService: MetadataElasticSearchService) {
    this.configureDragula();
    this.NEW_COLUMN_PLACEHOLDER = this.translate.instant('New column');
  }

  get isNameExist(): boolean {
    return this.existingCategoryNames.includes(this.f.categoryName.value);
  }

  get f() {
    return this.machineCategoryEditForm.controls;
  }

  ngOnInit(): void {
    this.generateMachineCategoryEditForm();
  }

  public changeIndexes(columns: CategoryColumn[]): void {
    let index = 0;
    for (const column of columns) {
      column.order = index;
      index++;
    }
  }

  public onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  public onCancel(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  public onSave(): void {
    //delete properties needed only on dashboard list view
    delete this.columnConfiguration.isCollapsed;
    delete this.columnConfiguration.machinesCount;
    const columnConfig: MachineCategoryColumnConfiguration = {...this.columnConfiguration};
    columnConfig.categoryColumns.map((c) => delete c['invalid']);
    this.machineCategoryService.updateColumnConfigCache(this.machineCategory.id, columnConfig);
    this.activeModal.close({category: this.machineCategory, config: columnConfig});
  }

  public onInputCategoryName(): void {
    this.machineCategory.name = this.f.categoryName.value;
    this.columnConfiguration.categoryName = this.f.categoryName.value;
    this.checkFormValidity();
  }

  public onInputDisplayName(): void {
    this.machineCategory.displayName = this.f.displayName.value.trim();
    this.columnConfiguration.categoryDisplayName = this.f.displayName.value.trim();
    this.checkFormValidity();
  }

  public onFileUploadComplete(content: string | ArrayBuffer | null): void {
    this.machineCategory.icon = String(content);
    this.columnConfiguration.categoryIcon = String(content);
    this.f.icon.setValue(content);
    this.checkFormValidity();
  }

  public onFileUploadError(error: ProgressEvent<FileReader>): void {
    this.notification.error('Error uploading image', error, this.COMPONENT_NAME, true);
  }

  public deleteIcon(): void {
    this.f.icon.reset();
    this.machineCategory.icon = '';
  }

  public onDeleteColumn(column: CategoryColumn): void {
    const index = this.columnConfiguration.categoryColumns.indexOf(column);
    if (index > -1) {
      this.columnConfiguration.categoryColumns.splice(index, 1);
    }
  }

  public isCollapsed(collapsed: boolean | undefined): boolean {
    if (collapsed === undefined) {
      return true;
    }
    return collapsed;
  }

  public onAddColumn(): void {
    const newColumn = new CategoryColumn();
    newColumn.collapsed = false;
    newColumn.label = this.NEW_COLUMN_PLACEHOLDER;
    newColumn.order = this.columnConfiguration.categoryColumns.length;
    newColumn['invalid'] = false;
    this.columnConfiguration.categoryColumns.push(newColumn);
    this.checkFormValidity();
  }

  private generateMachineCategoryEditForm(): void {
    const displayNamePattern = '^[A-Za-z0-9!& \'_\+-]{0,32}$';
    const categoryNamePattern = '^[A-Z0-9_]{0,32}$';
    this.machineCategoryEditForm = new UntypedFormGroup({
      categoryName: new UntypedFormControl(this.machineCategory.name, [Validators.required, Validators.pattern(categoryNamePattern)]),
      displayName: new UntypedFormControl(this.machineCategory.displayName, [Validators.required, Validators.pattern(displayNamePattern)]),
      icon: new UntypedFormControl(this.machineCategory.icon, [Validators.required]),
      referenceMachine: new UntypedFormControl()
    });
    this.checkFormValidity();

    if (!this.isInEditMode) {
      this.f.referenceMachine.disable();
      this.setDefaultColumns();
    }

    if (this.isInEditMode) {
      this.f.categoryName.disable();
      this.f.referenceMachine.valueChanges
      .pipe(
          switchMap((newRefMachine) => {
            this.isLoadingMachine = true;
            if (newRefMachine?.id !== this.columnConfiguration.referenceMachineId) {
              this.columnConfiguration.categoryColumns = this.columnConfiguration.categoryColumns.filter(({type}) => type === MachineCategoryBaseColumnTypes.DEFAULT);
              this.columnConfiguration.categoryColumns.map(c => c['invalid'] = false);
            }
            return this.setRefIdAndGetSignalsAndMetadata(newRefMachine);
          }),
          filter(res => !!res)
      ).subscribe((result) => {
        this.signalsForRefMachine = result.signals;
        this.metaDataForRefMachine = result.metadata?.result?.data;
        this.isLoadingMachine = false;
      });
      this.getMachinesByCategoryAndConfig();
    }
  }

  public isColumnInvalid(event: boolean, index: number): void {
    if (this.columnConfiguration.categoryColumns[index]) {
      this.columnConfiguration.categoryColumns[index]['invalid'] = event;
    }
    this.checkFormValidity();
  }

  public checkFormValidity(): void {
    this.setDuplicatedNames();
    this.isColumnFormInvalid = !!this.columnConfiguration.categoryColumns.find(c => c['invalid']);
    this.isSaveButtonDisabled = this.machineCategoryEditForm.invalid || this.isColumnFormInvalid || this.existingCategoryNames.includes(this.machineCategory.name);
  }

  private getMachinesByCategoryAndConfig(): void {
    this.isLoadingMachinesByCategory = true;
    this.elasticSearchService.listMachinesByCategory(this.machineCategory.name)
    .pipe(switchMap((machinesForCategory) => this.getColumnConfigurationAndSetMachinesForCategory(machinesForCategory)))
    .subscribe(
        {
          next: (success) => {
            this.columnConfiguration = new MachineCategoryColumnConfiguration(success);
            this.f.referenceMachine.setValue(this.machinesByCategory.find(machine => machine.id == success.referenceMachineId));
            this.isLoadingMachinesByCategory = false;
          }, error: () => {
            console.log('Error getting machines for category');
            this.isLoadingMachinesByCategory = false;
          }
        }
    );
  }

  private setDefaultColumns() {
    const defaultColumn = new CategoryColumn();
    defaultColumn.deletable = false;
    defaultColumn.type = MachineCategoryBaseColumnTypes.DEFAULT;
    defaultColumn.name = this.MACHINE_NAME_COLUMN;
    defaultColumn['invalid'] = false;
    this.columnConfiguration.categoryColumns.push(defaultColumn);
  }

  private getColumnConfigurationAndSetMachinesForCategory(machines: any): Observable<any> {
    this.machinesByCategory = machines.result.data;
    if (this.machineCategory.id) {
      return this.machineCategoryService.getColumnConfigurationById(this.machineCategory.id);
    }
    return of(null);
  }

  private setRefIdAndGetSignalsAndMetadata(newRefMachine: any): Observable<any> {
    this.columnConfiguration.referenceMachineId = newRefMachine?.id;

    if (newRefMachine?.id) {
      return forkJoin({
        signals: this.machineService.getSignals(newRefMachine.id).pipe(catchError(error => of(error))),
        metadata: this.elasticMetaDataService.listAllMachineMetaData(newRefMachine.id).pipe(catchError(error => of(error)))
      });
    }
    return of(null);
  }

  private configureDragula(): void {
    this.subscription.add(this.dragulaService.drag(this.dragAndDropColumns)
        .subscribe(({el}) => {
          el.classList.remove('ex-moved');
        })
    );

    this.subscription.add(this.dragulaService.drop(this.dragAndDropColumns)
        .subscribe(({el}) => {
          el.classList.add('ex-moved');
          this.changeIndexes(this.columnConfiguration.categoryColumns);
        })
    );

    this.subscription.add(this.dragulaService.over(this.dragAndDropColumns)
        .subscribe(({container}) => {
          container.classList.add('ex-over');
        })
    );
    this.subscription.add(this.dragulaService.out(this.dragAndDropColumns)
        .subscribe(({container}) => {
          container.classList.remove('ex-over');
        })
    );
  }

  private setDuplicatedNames(): void {
    const arr: string[] = [];
    this.validators.setDuplicatedNames(
        this.columnConfiguration.categoryColumns.reduce((acc, {name}) => {
          if (!arr.includes(name)) {
            arr.push(name);
          } else if (!acc.includes(name)) {
            acc.push(name);
          }
          return acc;
        }, [] as string[]));
  }
}
